import React from 'react';

import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ImageList,
  ImageListItem,
  Card,
  Button,
} from '@mui/material';

import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';

import CardMediaImage from '@/components/CardMediaImage';
import PhotoModal from '@/components/PhotoModal';
import { service } from '@/share';

const CustomListItem: React.FC<{
  name: string,
  text: string,
}> = ({ name, text }) => (
  <>
    <ListItem style={{ padding: '0px 0px 0px 10px' }}>
      <ListItemIcon style={{ minWidth: '120px' }}>{name}</ListItemIcon>
      <List
        style={{
          padding: '5px',
          lineBreak: 'anywhere',
          whiteSpace: 'pre-wrap',
        }}
      >
        <span>{text}</span>
      </List>
    </ListItem>
    <Divider />
  </>
);

const PageInfo: React.FC<{
  /** データ */
  data: PageJson,
  /** 機嫌を表示しない */
  hideCondition: boolean,
  /** 添付 */
  attachments?: File[],
}> = ({ data, attachments, hideCondition }) => {
  const [preview, setPreview] = React.useState<{
    s3Path: string,
    url: string | undefined
  } | undefined>(undefined);
  const attachmentsText = attachments
    ? attachments.map((file) => file.name)
    : [...data.data.images, ...data.data.attachments];
  const images = attachments
    ? attachments.map((file) => ({ name: file.name, url: URL.createObjectURL(file) }))
    : data.data.images.map((image) => ({ name: image, url: undefined }));
  const nurseryId = data.data.recorderType === 'app' ? data.pub.replyId : data.pub.sendId;
  const s3PathPrefix = `WagakoNurseryChildNotebook_PageEntity/${data.data.recordDate}#${data.data.recorderType}#${data.data.parentId}_${data.data.childId}/`;
  return (
    <List>
      {data.data.recorderType === 'app' && (
        <CustomListItem name="ステータス" text={data.data.isSent ? '送信済み' : '下書き'} />
      )}
      {data.data.recorderType === 'nursery' && (
        <CustomListItem name="記録者" text={data.data.recorderName} />
      )}
      {!hideCondition && (
        <CustomListItem name="機嫌" text={data.data.moodCategory} />
      )}
      {data.data.conditionType && (
        <CustomListItem name="体調" text={data.data.conditionType} />
      )}
      {data.data.conditionNote && data.data.conditionNote.length > 0 && (
        <CustomListItem name="体調メモ" text={data.data.conditionNote} />
      )}
      <CustomListItem name="コメント" text={data.data.comment} />
      {attachmentsText.length > 0 && (
        <>
          <ListItem style={{ padding: '0px 0px 0px 10px' }}>
            <ListItemIcon style={{ minWidth: '120px' }}>添付ファイル</ListItemIcon>
            <List style={{ padding: '5px' }}>
              {attachmentsText.map((name) => (
                <div key={name}>
                  <Button
                    style={{ textTransform: 'none' }}
                    variant="text"
                    onClick={async () => {
                      const blob = await service.file.getFileBlob(`${s3PathPrefix}${name}`, nurseryId);
                      if (blob) {
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.target = '_blank';
                        a.download = name;
                        a.click();
                      }
                    }}
                    disabled={attachments !== undefined}
                  >
                    {name}
                  </Button>
                </div>
              ))}
            </List>
          </ListItem>
          <Divider />
        </>
      )}
      {images.length > 0 && (
        <ImageList cols={images.length < 2 ? 1 : 2} style={{ marginTop: '10px' }}>
          {images.map((v) => (
            <ImageListItem
              key={v.name}
            >
              <Card style={{ margin: 'auto' }}>
                <CardMediaImage
                  nurseryId={nurseryId}
                  s3Path={`${s3PathPrefix}${v.name}_thumbnail.webp`}
                  onClick={() => setPreview({ s3Path: `${s3PathPrefix}${v.name}`, url: v.url })}
                  alt={v.name}
                  objectFit="cover"
                  dataUrl={v.url}
                />
              </Card>
            </ImageListItem>
          ))}
        </ImageList>
      )}
      {preview && (
        <PhotoModal
          s3Path={preview.s3Path}
          dataUrl={preview.url}
          close={() => setPreview(undefined)}
          nurseryId={nurseryId}
        />
      )}
    </List>
  );
};

export default PageInfo;
